import { Box, Drawer, Stack, Typography } from "@mui/material";
import React from "react";
import { useCallback } from "react";
import {
	BooleanField,
	Datagrid,
	DateField,
	FunctionField,
	ImageField,
	Link,
	List,
	NumberField,
	TextField,
	UrlField,
	useRecordContext,
	useTranslate,
} from "react-admin";
import { matchPath, matchRoutes, useNavigate } from "react-router-dom";
import ArgumentEdit from "../../debate/arguments/ArgumentEdit";
import rowStyle from "../../debate/arguments/rowStyle";
import CustomPagination from "../../utils/CustomPagination";
import ModerationBulkActionButtons from "../../utils/ModerationBulkActionsButtons";
import AuthorField from "../AuthorField";
import type { ModerableSource } from "../moderable_sources/moderableSource";
import { providerIcons } from "../oauth_providers/icons";
import ModerableContentFilter from "./ModerableContentFilter";
import type { ModerableContent } from "./moderableContent";

function SourceField({ label }: { label: string }) {
	const record = useRecordContext();

	if (!record?.post) {
		return null;
	}

	return (
		<Link
			to={record?.post.url}
			target="_blank"
			sx={{
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				maxWidth: 200,
			}}
			title={record?.post.title || record?.post.body || "[Untitled]"}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="stretch"
				gap={1}
			>
				<img
					src={providerIcons[record?.provider as ModerableSource["provider"]]}
					style={{ maxWidth: "25px" }}
					alt=""
				/>
				<Typography
					sx={{
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						maxWidth: "400px",
						textDecoration: "underline",
						color: "blue",
					}}
				>
					{record?.post.title || record?.post.body || "[Untitled]"}
				</Typography>
			</Stack>
		</Link>
	);
}

function AttachmentField({ label }: { label: string }) {
	const record = useRecordContext<ModerableContent>();

	if (!record?.attachment_type) {
		return (
			<Box minWidth={100} display="grid" sx={{ placeItems: "center" }}>
				-
			</Box>
		);
	}

	if (record.attachment_type === "image") {
		return (
			<img
				style={{ width: 200, height: "auto" }}
				src={record.attachment_url}
				alt={record.attachment_url}
			/>
		);
	}

	if (record.attachment_type === "video") {
		return (
			<video width="200" height="auto" controls>
				<source src={record.attachment_url} type="video/mp4" />
				<track
					kind="captions"
					src={record.attachment_url.replace(".mp4", ".vtt")}
				/>
			</video>
		);
	}

	if (record.attachment_type === "shared_link") {
		return (
			<Link target="_blank" rel="noreferrer" to={record.attachment_url}>
				<Box
					maxWidth={250}
					sx={{
						wordBreak: "break-all",
						textDecoration: "underline",
						color: "blue",
					}}
				>
					{record.attachment_url}
				</Box>
			</Link>
		);
	}
}

export const ModerableContentsList = () => {
	const translate = useTranslate();
	const navigate = useNavigate();
	const handleClose = useCallback(() => {
		navigate(`/${window.location.pathname.split("/")[1]}`);
	}, [navigate]);

	const routes = [{ path: "/moderable_contents/:id" }];
	const [{ route }] = matchRoutes(routes, window.location) || [
		{ path: "/messages" },
	];
	const match = route && matchPath(route.path, window.location.pathname);

	return (
		<div id="social-networks-root">
			<header style={{ marginBottom: "20px" }}>
				<h1>
					{translate("pos.social_networks.moderable_contents.section_title")}
				</h1>
				<p>
					{translate("pos.social_networks.moderable_contents.section_subtitle")}
				</p>
			</header>

			<List
				resource="moderable_contents"
				title="resources.moderable_contents.section_title"
				filters={<ModerableContentFilter />}
				sort={{ field: "created_at", order: "DESC" }}
				pagination={<CustomPagination />}
				sx={{ marginRight: "6px" }}
			>
				<Datagrid
					rowSx={rowStyle}
					optimized
					bulkActionButtons={
						<ModerationBulkActionButtons
							resourceName="moderable_contents"
							getElementId={undefined}
						/>
					}
				>
					<TextField source="id" label="ID" />

					<AuthorField label="Author" />

					<TextField
						source="content"
						label=""
						sx={{
							display: "block",
							width: 400,
							wordBreak: "break-all",
						}}
					/>

					<AttachmentField
						label={translate("resources.moderable_contents.fields.attachment")}
					/>

					<SourceField label="Source" />

					<BooleanField
						source="parent_uid"
						looseValue
						label={translate("resources.messages.fields.is_reply")}
					/>

					<NumberField
						source="moderation_entry.moderation_score"
						label={translate("resources.messages.fields.moderation_score")}
						transform={(v) => Math.floor(100 - v) / 100}
						options={{ style: "percent" }}
					/>
					<DateField
						source="published_at"
						label={translate(
							"resources.moderable_contents.fields.published_at",
						)}
						showTime
					/>

					<DateField
						source="created_at"
						label={translate("resources.moderable_contents.fields.created_at")}
						showTime
					/>
					<FunctionField
						label={translate("resources.moderable_contents.fields.status")}
						render={(record: Record<string, { status: string }>) =>
							record?.moderation_entry?.status === "rejected"
								? translate("resources.messages.status.rejected")
								: record?.moderation_entry?.status === "accepted"
									? translate("resources.messages.status.accepted")
									: translate("resources.messages.status.pending")
						}
					/>
				</Datagrid>
			</List>
			<Drawer
				open={!!match}
				anchor="right"
				onClose={handleClose}
				sx={{ zIndex: 100 }}
			>
				{/* To avoid any errors if the route does not match, we don't render at all the component in this case */}
				{!!match && (
					<ArgumentEdit id={match.params.id} onCancel={handleClose} />
				)}
			</Drawer>
		</div>
	);
};
